import { cn } from '@/lib/utils';
import type { Scorecard } from '@/types/analysis';
import {
  isOriginalityExcluded,
  originalityExclusionNotice,
  originalityVolatilityValue,
} from '@/utils/originalityDisplay';
import {
  ECHO_THRESHOLD_CLEARED,
  clearanceFromEchoScore,
  echoScoreDisplay,
} from '@/config/indexThresholds';
import { formatScriptId } from '@/utils/formatIndexId';
import AttentionCurveChart from './AttentionCurveChart';

type ForensicMetric = {
  label: string;
  value: number | null;
  displayValue?: string;
  accent?: 'green' | 'gold' | 'neutral';
};

function pct(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(v);
}

function avg(values: (number | null)[]): number | null {
  const valid = values.filter((x): x is number => x != null);
  if (!valid.length) return null;
  return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
}

function buildMetrics(scorecard: Scorecard): ForensicMetric[] {
  const v7 = scorecard.v7_scores ?? scorecard.v6_scores;
  const structural = avg([
    pct(v7?.story_spine_pct),
    pct(v7?.etb_structure_pct),
    pct(v7?.pacing_pct),
  ]);
  const attention = avg([
    pct(v7?.scene_strength_pct),
    pct(v7?.story_beat_strength_pct),
    pct(v7?.concentration_pct),
  ]);
  const commercial = pct(v7?.commercial_viability_pct ?? v7?.target_market_pct);
  const volatility = originalityVolatilityValue(scorecard);
  const mandateFit = pct(v7?.genre_promise_pct ?? v7?.thematic_stakes_pct);

  return [
    { label: 'Genre promise', value: mandateFit, accent: 'green' },
    { label: 'Attention resonance', value: attention, accent: 'green' },
    { label: 'Structural integrity', value: structural, accent: 'green' },
    { label: 'Commercial signal', value: commercial, accent: 'gold' },
    {
      label: 'Narrative volatility',
      value: volatility,
      accent: 'neutral',
      displayValue: isOriginalityExcluded(scorecard) ? 'not applicable' : undefined,
    },
  ];
}

type ForensicScorecardProps = {
  scorecard: Scorecard;
  scriptTitle: string;
  sessionId: string;
  onExport?: () => void;
  className?: string;
};

export default function ForensicScorecard({
  scorecard,
  scriptTitle,
  sessionId,
  onExport,
  className,
}: ForensicScorecardProps) {
  const overall = scorecard.overall_engagement_score;
  const display = echoScoreDisplay(overall);
  const clearance = clearanceFromEchoScore(overall);
  const scriptId = formatScriptId(sessionId);
  const metrics = buildMetrics(scorecard);
  const originalityNotice = originalityExclusionNotice(scorecard);
  const delta =
    display != null ? (display - ECHO_THRESHOLD_CLEARED / 10).toFixed(1) : null;

  const clearanceLabel =
    clearance === 'cleared'
      ? 'Cleared for board review'
      : clearance === 'rewrite'
        ? 'Rewrite recommended'
        : clearance === 'high_risk'
          ? 'High risk — review required'
          : 'Pending audit';

  return (
    <div className={cn('space-y-6', className)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="ni-label mb-1">Nuclius Index™</p>
          <p className="text-xs font-mono text-white/45">{scriptId} · {scriptTitle}</p>
          <p className="text-[10px] font-mono uppercase text-white/35 mt-1">
            {clearance === 'cleared' ? 'AUDITED' : clearance.toUpperCase().replace('_', ' ')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-4xl md:text-5xl font-sans font-bold tabular-nums text-white">
            {display != null ? display.toFixed(1) : '—'}
          </p>
          {delta != null && (
            <p
              className={cn(
                'text-xs font-mono mt-1',
                Number(delta) >= 0 ? 'text-index-cleared' : 'text-index-risk',
              )}
            >
              {Number(delta) >= 0 ? '▲' : '▼'} {Math.abs(Number(delta))} vs threshold
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            clearance === 'cleared' && 'bg-index-cleared',
            clearance === 'rewrite' && 'bg-index-rewrite',
            clearance === 'high_risk' && 'bg-index-risk',
          )}
        />
        <span className="text-[10px] font-mono uppercase tracking-wider text-index-cleared">
          {clearanceLabel}
        </span>
        <span className="text-[10px] font-mono text-white/30 ml-2">High · σ 0.04</span>
      </div>

      <AttentionCurveChart scriptId={scriptId} scorecard={scorecard} />

      {originalityNotice && (
        <div className="ni-panel border-index-rewrite/40 p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-index-rewrite mb-2">
            Originality flag
          </p>
          <p className="text-sm font-sans text-white/70 leading-relaxed whitespace-pre-line">
            {originalityNotice}
          </p>
        </div>
      )}

      <div className="ni-panel p-4 space-y-4">
        {metrics.map((m) => (
          <div key={m.label}>
            <div className="flex justify-between text-[10px] font-mono uppercase tracking-wider text-white/45 mb-1">
              <span>{m.label}</span>
              <span>
                {m.displayValue ?? (m.value != null ? `${m.value}%` : 'N/A')}
              </span>
            </div>
            <div className="h-1.5 bg-[#1f1f1f] rounded-sm overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-sm transition-all',
                  m.accent === 'gold' && 'bg-index-rewrite',
                  m.accent === 'green' && 'bg-index-cleared',
                  m.accent === 'neutral' && 'bg-white/30',
                )}
                style={{ width: `${m.value ?? 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {onExport && (
        <button
          type="button"
          onClick={onExport}
          className="w-full ni-panel border-index-accent/40 py-3 text-center text-xs font-mono uppercase tracking-[0.2em] text-index-accent hover:bg-index-accent/5 transition-colors"
        >
          Export forensic scorecard
          <span className="block text-[10px] text-white/25 mt-1">F12</span>
        </button>
      )}
    </div>
  );
}
